
import React, { useState, useEffect, useMemo } from "react";
import { CardElement, useStripe, useElements, Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { useAuth } from "context/AuthProvider";
import { CreditCardService } from "services/CreditCardService";
import { Modal, Button, Label, Form, FormGroup, Input, ModalBody } from "reactstrap";
import { toast } from "react-toastify";
import { environment } from "environment/environment";
import { Puff } from "react-loader-spinner";
import TableContainer from "components/Common/TableContainer";
import { NotificationData, useNotification } from "context/NotificationProvider";
import { NotificationService } from "services/NotificationService";
import Swal from "sweetalert2"
import { AuthService } from "services/AuthService";
import { CreditCards } from "models/CreditCard";
toast.configure();

const USE_STRIPE_TEST_CARD =
  !environment.STRIPE_PUBLIC_KEY ||
  environment.STRIPE_PUBLIC_KEY.includes("placeholder");
const stripePromise = USE_STRIPE_TEST_CARD
  ? Promise.resolve(null)
  : loadStripe(environment.STRIPE_PUBLIC_KEY);

const PaymentCardsInner: React.FC = () => {

  const [user, setUser] = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [cardholderEmail, setCardholderEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [cardholderName, setCardholderName] = useState<string>('');
  const [attachedCards, setAttachedCards] = useState<CreditCards[]>([]);
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({ email: "", name: "" });
  const [isloading, setIsloading] = useState<boolean>(true);
  const [notification, setNotification] = useNotification() as NotificationData;
  const [isAutomaticPaymentEnabled, setIsAutomaticPaymentEnabled] = useState<boolean>(false);

  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    if (user) {
      setIsAutomaticPaymentEnabled(!!user?.isAutoInvoicePayment);
    }
  }, [user?.isAutoInvoicePayment]);

  useEffect(() => {
    AuthService.getMe()
      .then((res) => {
        if (res.success) {
          setUser(res.data);
        } else {
          console.warn("Failed to fetch user:", res.message || "Unknown error");
        }
      })
      .catch((error) => {
        console.error("Error fetching user data:", error);
      });
  }, []);

  const validateEmail = (value: string) => {
    if (!value) return "Email is required";
    if (!/\S+@\S+\.\S+/.test(value)) return "Invalid email address";
    return "";
  };
  const validateName = (value: any) => {
    if (!value) return "Cardholder name is required";

    return "";
  };


  const handleAddPaymentCard = async () => {
    if (!user?.email) {
      console.error("User email is missing!");
      return;
    }

    if (attachedCards.length >= 3) {
      toast.error("You have reached the maximum limit of 3 cards.", {
        position: toast.POSITION.BOTTOM_RIGHT,
        className: "foo-bar",
        style: { marginBottom: "4rem" },
      });
      setShowModal(false);
      return;
    }

    setShowModal(true);
  };


  const addAndAttachPaymentMethod = async () => {
    const emailError = validateEmail(cardholderEmail);
    const nameError = validateName(cardholderName);
    if (emailError || nameError) {
      setFormErrors({ email: emailError, name: nameError });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const customerEmail = cardholderEmail || user?.email;
      if (!customerEmail) {
        throw new Error("Email is required");
      }

      await CreditCardService.createStripeCustomer({
        email: customerEmail,
      });

      if (USE_STRIPE_TEST_CARD) {
        const response = await CreditCardService.createAndAttchPaymentMethod({
          paymentMethodId: "tok_visa",
          useTestCard: true,
          billingDetails: {
            email: cardholderEmail,
            name: cardholderName,
          },
        });

        if (!response.success) {
          throw new Error("Could not save the test payment card.");
        }
      } else {
        if (!stripe || !elements) {
          setError("Stripe is not loaded");
          return;
        }

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) throw new Error('Card details are missing');
        const { paymentMethod, error } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          billing_details: {
            email: cardholderEmail,
            name: cardholderName,
          },
        });

        if (error) {
          throw new Error(error.message);
        }

        if (!paymentMethod) {
          throw new Error('Payment method creation failed');
        }

        const response = await CreditCardService.createAndAttchPaymentMethod({
          paymentMethodId: paymentMethod.id,
          billingDetails: {
            email: cardholderEmail,
            name: cardholderName,
          },
        });

        if (!response.success) {
          throw new Error("error occured");
        }
      }

      setShowModal(false);
      setCardholderEmail("");
      setCardholderName("");

      NotificationService.getNotifications(5, 0).then(res => {
        if (res.count !== undefined) {
          setNotification(res.count);
        }
      });
      toast.success("Successfully added Payment card", {
        position: toast.POSITION.BOTTOM_RIGHT,
        className: "foo-bar",
        style: { marginBottom: "4rem" },
      });
      await fetchAttachedCards();

    } catch (error) {
      setError(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchAttachedCards();
  }, []);

  const fetchAttachedCards = async () => {

    try {
      setIsloading(true);
      const paymentCards = await CreditCardService.getAttachedPaymentMethods();

      if (paymentCards.data.length > 0) {
        setAttachedCards(paymentCards.data);
        setIsloading(false)
      } else {
        setAttachedCards([]);
        setIsloading(false)
      }
    } catch (error) {
      console.error("Error fetching payment methods:", error);
      setIsloading(false)
    }
  };


  const handleDelete = async (paymentMethodId: string) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#F75D21",
      cancelButtonColor: "#A9A9A9",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        if (!paymentMethodId) {
          console.error("Payment method ID is required");
          return;
        }


        const response = await CreditCardService.deleteCustomerPaymentMethods(paymentMethodId);
        if (response.success) {
          console.log("Payment method deleted successfully");

          setAttachedCards((prevCards) =>
            prevCards.filter((card) => card.id !== paymentMethodId)
          );

          toast.success("Payment method deleted successfully!", {
            position: toast.POSITION.BOTTOM_RIGHT,
            className: "foo-bar",
            style: { marginBottom: "4rem" },
          });
        } else {
          console.error("Failed to delete payment method:", response);

          toast.error("Failed to delete payment method", {
            position: toast.POSITION.BOTTOM_RIGHT,
            className: "foo-bar",
            style: { marginBottom: "4rem" },
          });
        }
      } catch (error) {
        console.error("Error deleting payment method:", error);

        toast.error("An unexpected error occurred", {
          position: toast.POSITION.BOTTOM_RIGHT,
          className: "foo-bar",
          style: { marginBottom: "4rem" },
        });
      }
    }
  };

  const handleSetDefault = async (paymentMethodId: string) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You are about to change the default payment method.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#F75D21",
      cancelButtonColor: "#A9A9A9",
      confirmButtonText: "Yes, change it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await CreditCardService.changeDefaultPaymentCard(paymentMethodId);

        if (response?.success) {
          setAttachedCards(prevCards =>
            prevCards.map(card => ({
              ...card,
              isDefault: card.id === paymentMethodId
            }))
          );
          const notificationResponse = await NotificationService.getNotifications(5, 0);
          if (notificationResponse.count !== undefined) {
            setNotification(notificationResponse.count);
          }
          toast.success("Default payment method updated successfully!", {
            position: toast.POSITION.BOTTOM_RIGHT,
            className: "foo-bar",
            style: { marginBottom: "4rem" },
          });
        } else {
          toast.error("Failed to update default payment method", {
            position: toast.POSITION.BOTTOM_RIGHT,
            className: "foo-bar",
            style: { marginBottom: "4rem" },
          });
        }
      } catch (error) {
        toast.error("An unexpected error occurred", {
          position: toast.POSITION.BOTTOM_RIGHT,
          className: "foo-bar",
          style: { marginBottom: "4rem" },
        });
      }
    }
  };


  const handleToggleAutoPayment = async () => {
    const newStatus = !isAutomaticPaymentEnabled;

    const result = await Swal.fire({
      title: `Are you sure you want to ${newStatus ? 'enable' : 'disable'} automatic payments?`,
      text: newStatus
        ? "You will be automatically charged for any new invoices."
        : "You will need to manually pay for new invoices.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#F75D21",
      cancelButtonColor: "#A9A9A9",
      confirmButtonText: "Yes, " + (newStatus ? 'enable' : 'disable') + " it!",
    });

    if (result.isConfirmed) {
      try {
        const success = await CreditCardService.updateAutoPayment({
          isAutoInvoicePayment: newStatus
        });

        if (success) {
          setIsAutomaticPaymentEnabled(newStatus);
          toast.success(
            `Automatic payments ${newStatus ? 'enabled' : 'disabled'}`,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
              className: "foo-bar",
              style: { marginBottom: "4rem" },
            }
          );
        } else {
          toast.error("Failed to update automatic payment settings", {
            position: toast.POSITION.BOTTOM_RIGHT,
            className: "foo-bar",
            style: { marginBottom: "4rem" },
          });
        }
      } catch (error) {
        console.error("Error updating automatic payment settings:", error);
        toast.error("An error occurred while updating settings", {
          position: toast.POSITION.BOTTOM_RIGHT,
          className: "foo-bar",
          style: { marginBottom: "4rem" },
        });
      }
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Card Type",
        accessor: "brand",
        Cell: ({ value }: { value: string }) => (
          <div className="text-center">
            {value.charAt(0).toUpperCase() + value.slice(1)}
          </div>
        )
      },
      {
        Header: "Card Number",
        accessor: "last4",
        Cell: ({ value }: { value: string }) => (
          <div className="text-center">**** **** **** {value}</div>
        )
      },
      {
        Header: "Expiration Date",
        Cell: ({ row }: { row: { original: CreditCards } }) => (
          <div className="text-center">
            {row.original.exp_month.toString().padStart(2, '0')}/{row.original.exp_year.toString().slice(-2)}
          </div>
        )
      },
      {
        Header: "Actions",
        accessor: "id",
        Cell: ({ value }: { value: string }) => (
          <div className="text-center">
            <button
              className="border-0 bg-transparent p-2"
              onClick={() => {
                handleDelete(value).catch((error) => {
                  console.error("Error deleting payment method:", error);
                });
              }}
            >
              <i
                className="bx bx-trash"
                style={{
                  color: "orange",
                  border: "none",
                  outline: "none",
                  fontSize: '1.2rem'
                }}
              ></i>
            </button>
          </div>
        ),
        disableSortBy: true
      },
      {
        Header: "Set Default",
        Cell: ({ row }: { row: { original: CreditCards } }) => (
          <div className="text-center" style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="radio"
                name="defaultPaymentMethod"
                value={row.original.id}
                checked={row.original.isDefault}
                onChange={() => handleSetDefault(row.original.id)}
                style={{
                  display: 'none',
                }}
              />
              <span
                style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  border: '2px solid orange',
                  display: 'inline-block',
                  position: 'relative',
                }}
              >
                {row.original.isDefault && (
                  <span
                    style={{
                      width: '8px',
                      height: '8px',
                      backgroundColor: 'orange',
                      borderRadius: '50%',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}
                  />
                )}
              </span>
            </label>
          </div>
        ),
        disableSortBy: true,
      },
    ],
    []
  );

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Cards" breadcrumbItem="Create Cards" />

        <div className="shadow-md p-3 bg-white">

          <div className="d-flex d-lg-flex d-md-flex justify-content-center justify-content-lg-end justify-content-md-end">

            <Button
              color="primary"
              size="sm"
              className="px-5 me-lg-2 me-md-2 mb-2 ms-1"

              onClick={() => {
                handleAddPaymentCard().catch((error) => {
                  console.error("Error in handleRememberCard:", error);
                });
              }}
            >
              Add Payment Cards
            </Button>
          </div>

          {isloading ? <div className="d-flex justify-content-center align-items-center" style={{ height: '500px' }}>
            <Puff color="#FFD0B8" height={50} width={50} />
          </div> :
            <>

              {attachedCards.length > 0 ? (
                <>
                  <TableContainer
                    columns={columns}
                    data={attachedCards}
                    currentPage={undefined}
                    pagelimit={undefined}
                    handlePageChange={undefined}
                    handleColumnChange={undefined}
                    selectedColumns={undefined}
                    showColumnChange={false}
                    handleSearch={() => { }}
                    isGlobalFilter={false}
                    isAddOptions={false}
                    customPageSize={100}
                    className="custom-header-css"
                    isJobListGlobalFilter={undefined}
                    isAddUserList={undefined}
                    handleOrderClicks={undefined}
                    handleUserClick={undefined}
                    handleCustomerClick={undefined}
                    isAddCustList={undefined}
                    handleSendEmail={undefined}
                    isPDFGenerate={false}
                    isAddressBook={false}
                    ispopup={false}
                    hancleDeleteRecords={false}
                    handleSelect={undefined}
                    selectedID={undefined}
                    hasCheckBox={false}
                    footerData={undefined}
                  />
                  {user?.paymentOption === 'monthly' &&
                    <div className="d-flex justify-content-end align-items-center mt-3 gap-2">
                      <span className="fw-medium">Automatic Monthly Invoice Payments</span>
                      <div
                        onClick={handleToggleAutoPayment}
                        style={{
                          position: 'relative',
                          width: '60px',
                          height: '26px',
                          borderRadius: '13px',
                          backgroundColor: isAutomaticPaymentEnabled ? '#4CAF50' : '#ff4444',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div
                          style={{
                            position: 'absolute',
                            top: '3px',
                            left: isAutomaticPaymentEnabled ? '37px' : '3px',
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            backgroundColor: 'white',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            zIndex: 1
                          }}
                        />
                        <span
                          style={{
                            fontSize: '10px',
                            fontWeight: 'bold',
                            color: isAutomaticPaymentEnabled ? 'white' : '#ff4444',
                            opacity: isAutomaticPaymentEnabled ? 1 : 0,
                            transition: 'opacity 0.2s ease',
                            userSelect: 'none',
                            marginInlineStart: '10px'
                          }}
                        >
                          ON
                        </span>
                        <span
                          style={{
                            fontSize: '10px',
                            fontWeight: 'bold',
                            color: isAutomaticPaymentEnabled ? '#ff4444' : 'white',
                            opacity: isAutomaticPaymentEnabled ? 0 : 1,
                            transition: 'opacity 0.2s ease',
                            userSelect: 'none',
                            marginInlineEnd: '10px'
                          }}
                        >
                          OFF
                        </span>
                      </div>
                    </div>
                  }
                </>
              ) : (

                <p className="text-center">No payment cards found. Please add a payment card.</p>

              )}</>}
        </div>

        <div className="shadow-md p-3 bg-white mt-4 p-5" style={{ fontSize: '0.9rem' }}>
          <h6 className="fw-bold">Saved Cards</h6>
          <p style={{ fontSize: '0.95rem' }}>Securely store and manage your payment methods.</p>

          <h6 className="fw-bold mt-3">Personal Customers</h6>
          <p style={{ fontSize: '0.95rem' }}>Pay instantly at checkout. Save a card for added convenience, and use it for current and future purchases.</p>

          <h6 className="fw-bold mt-3">Business Customers</h6>
          <p style={{ fontSize: '0.95rem' }}>Saving a card does not result in a charge. Your saved card can be used for invoice payments - either manually or automatically if Auto-Pay for invoices is enabled.</p>
        </div>

      </div>




      <Modal isOpen={showModal} toggle={() => setShowModal(false)}>
        <div className="modal-header">
          <h5 className="modal-title">Add Payment Card</h5>
          <Button close onClick={() => setShowModal(false)} />
        </div>
        <ModalBody>
          <Form autoComplete="off">
            {error && (
              <div className="text-danger mb-3">
                {error}
              </div>
            )}
            <FormGroup>
              <Label for="cardholderEmail">Email:</Label>
              <Input
                autoComplete="off"
                type="email"
                id="cardholderEmail"
                value={cardholderEmail}
                onChange={(e) => {
                  const value = e.target.value;
                  setCardholderEmail(value);
                  setFormErrors((prev) => ({ ...prev, email: validateEmail(value) }));
                }}
                invalid={!!formErrors.email}
              />
              {formErrors.email && <div className="text-danger small">{formErrors.email}</div>}
            </FormGroup>
            <FormGroup>
              <Label for="cardholderName">Cardholder Name:</Label>
              <Input
                autoComplete="off"
                type="text"
                id="cardholderName"
                value={cardholderName}
                onChange={(e) => {
                  const value = e.target.value;
                  setCardholderName(value);
                  setFormErrors((prev) => ({ ...prev, name: validateName(value) }));
                }}
                invalid={!!formErrors.name}
              />
              {formErrors.name && <div className="text-danger small">{formErrors.name}</div>}
            </FormGroup>
            <FormGroup>
              <Label>Card Details:</Label>
              {USE_STRIPE_TEST_CARD ? (
                <div className="alert alert-warning mb-0">
                  Local Stripe publishable key is not configured. Saving will attach Stripe test Visa <strong>4242</strong>.
                </div>
              ) : (
              <div style={{ border: '1px solid #ccc', borderRadius: '4px', padding: '10px' }}>
                <CardElement

                  options={{
                    hidePostalCode: true,
                    style: {
                      base: {
                        fontSize: '18px',
                        color: '#32325d',
                        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                        '::placeholder': {
                          color: '#aab7c4',
                        },
                        padding: '10px',
                        backgroundColor: 'white',
                      },
                      invalid: {
                        color: '#fa755a',
                        iconColor: '#fa755a',
                      },
                    },
                  }}
                />
              </div>
              )}
            </FormGroup>
            <Button
              type="button"
              color="primary"
              onClick={() => {
                addAndAttachPaymentMethod()
              }}
              disabled={(!USE_STRIPE_TEST_CARD && !stripe) || loading}
            >
              {loading ? "Processing..." : "Save Card"}
            </Button>
          </Form>
        </ModalBody>
      </Modal>
    </div>
  );
};

const PaymentCards: React.FC = () => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentCardsInner />
    </Elements>
  );
};

export default PaymentCards;